import {
  BackendTrajectoryInfo,
  mapBackendTrajectoryToTrajectory,
  TrajectoryInfo,
} from './Trajectory';
import { Group } from './Group';
import each from 'lodash/fp/each';
import { Grade } from './Grade';

export type VersionRequest = {
  title: string;
};

export type TrajectoryOwner = {
  id: string;
  firstName: string;
  name: string;
};

export type Differentiation = TrajectoryInfo & {
  visibilities: Visibility[];
};

export type Visibility = {
  teacherId: string;
  trajectoryId: string;
  groupId: string;
  isVisible: boolean;
};

export type AlternativeTitle = {
  groupId: string;
  alternativeTitle: string;
  teacherId: string;
  trajectoryId: string;
  isAlternativeTitle: boolean;
};

// Backend

export type BackendDifferentiation = BackendTrajectoryInfo & {
  visibilities: {
    data: BackendVisibility[];
  };
};

export type BackendVisibility = {
  teacher_id: string;
  trajectory_id: string;
  group_id: string;
  is_visible: boolean;
};

export type BackendPreferredDifferentiation = {
  preferred_id: string;
  teacher_id: string;
  trajectory_id: string;
};

export type PreferredDifferentiation = {
  preferredId: string;
  teacherId: string;
  trajectoryId: string;
};

export type GradeWithGroups = Grade & { groups: Group[] };
export type GradesWithGroups = Record<string, GradeWithGroups>;

export const mapGroupsInGrades = (groups: Group[]): GradesWithGroups => {
  const mappedGroups: GradesWithGroups = {};
  each((group: Group) => {
    const grade = !mappedGroups[group.grade.id]
      ? (mappedGroups[group.grade.id] = { ...group.grade, groups: [] })
      : mappedGroups[group.grade.id];
    grade.groups.push(group);
  })(groups);
  return mappedGroups;
};

export type BackendAlternativeTitle = {
  group_id: string;
  alternative_title: string;
  trajectory_id: string;
  teacher_id: string;
  is_alternative_title: boolean;
};

// MAPPER

const mapBackendVisibilityToVisibility = (data: BackendVisibility): Visibility => ({
  teacherId: data.teacher_id,
  trajectoryId: data.trajectory_id,
  groupId: data.group_id,
  isVisible: Boolean(data.is_visible),
});

const mapBackendDifferentiationToDifferentiation = (
  data: BackendDifferentiation
): Differentiation => ({
  id: data.id,
  number: data.number,
  title: data.title,
  parent: data.parent ? mapBackendTrajectoryToTrajectory(data.parent.data) : null,
  visibilities: data.visibilities.data.map((data: BackendVisibility) =>
    mapBackendVisibilityToVisibility(data)
  ),
  isDraft: data.is_draft,
  isFrozen: data.is_frozen,
  isCollab: data.is_collab,
  isOwner: data.is_owner,
  isNotVisible: data.is_not_visible,
  owner: data.owner?.data,
  isPreferred: data.is_preferred,
  isPreview: data.is_preview,
});

export const getDifferentiationsMapper = (response: any): Differentiation[] =>
  response.data.map(mapBackendDifferentiationToDifferentiation);

const mapBackendPreferredDifferentiationToPreferredDifferentiation = (
  data: BackendPreferredDifferentiation
): PreferredDifferentiation => ({
  preferredId: data.preferred_id,
  teacherId: data.teacher_id,
  trajectoryId: data.trajectory_id,
});

export const preferredDifferentiationMapper = (response: {
  data?: BackendPreferredDifferentiation;
}): PreferredDifferentiation | null =>
  response.data 
    ? mapBackendPreferredDifferentiationToPreferredDifferentiation(response.data)
    : null;

export const mapBackendAlternativeTitleToAlternativeTitle = (
  data: BackendAlternativeTitle
): AlternativeTitle => ({
  groupId: data.group_id,
  alternativeTitle: data.alternative_title,
  trajectoryId: data.trajectory_id,
  teacherId: data.teacher_id,
  isAlternativeTitle: data.is_alternative_title,
});
